import { useEffect, useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { ThemeProvider } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import { useMainTheme } from "@fuse/core/FuseSettings/hooks/fuseThemeHooks";
import useThemeMediaQuery from "@fuse/hooks/useThemeMediaQuery";
import AppDialogDefault from "@/components/ui/appDialogDefault/AppDialogDefault.component";
import useUser from "@auth/user/useUser";
import {
	useListarAuditoriaRecurso,
	useListarGruposUsuario,
	useSalvarRestricoesGrupo,
} from "@/api/space/hooks/useSpace.authenticator";
import { keys_permissoes, useVerificaRecursosDaTela } from "@/permissoes/hooks/usePermissoes";
import {
	AdapterRecursoDaTelaComoArvore,
	calcularDelta,
	construirMapaEstado,
	montarResumoAlteracoes,
} from "@/permissoes/restricoes/restricoes.utils";
import DialogConfirmarSalvar from "@/permissoes/restricoes/DialogConfirmarSalvar";
import DialogConfirmarDescarte from "@/permissoes/restricoes/DialogConfirmarDescarte";
import type { ISpaceVerificaRecursoRequest } from "@/api/space/dtos/authenticator.dto";
import ModalAuditoriaGrupo from "@/app/(protected)/apps/configuracoes-gerais/components/cadastro-restricoes/ui/dialogs/auditoria/ModalAuditoriaGrupo";
import type { FiltroAuditoriaAplicado } from "@/app/(protected)/apps/configuracoes-gerais/components/cadastro-restricoes/cadastroRestricoes.types";
import CorpoRestricoesModal from "./CorpoRestricoesModal";
import RodapeAcoesModal from "./RodapeAcoesModal";
import { contarPermissoesAtivas, filtrarGruposPorBusca, montarArvoreRecurso } from "../restricoesRecursoModal.utils";
import { useCan } from "@/permissoes/hooks/useCan";
import { PERMISSOES_RESTRICOES } from "@/permissoes/restricoes/restricoes.constants";

// ------------------------------- CONSTANTS -------------------------------
const LIMITE_AUDITORIA_POR_PAGINA = 20;

const FILTROS_AUDITORIA_INICIAL: FiltroAuditoriaAplicado & { pagina: number } = {
	tipo: "todos",
	dataInicio: "",
	dataFim: "",
	pagina: 1,
};

// --------------------------------- TYPES ---------------------------------
type RestricoesRecursoModalProps = {
	readonly aberto: boolean;
	readonly tagRecurso: string;
	readonly onFechar: () => void;
};

// ------------------------------- COMPONENT -------------------------------
export default function RestricoesRecursoModal({
	aberto,
	tagRecurso,
	onFechar,
}: Readonly<RestricoesRecursoModalProps>) {
	// -------------------------------- STATES --------------------------------
	const queryClient = useQueryClient();
	const { data: user } = useUser();
	const permissaoSalvar = useCan(PERMISSOES_RESTRICOES.salvar);

	// Reaplica o tema principal: o modal e renderizado dentro do ToolbarTheme (paleta escura/texto branco),
	// que atravessa o portal do Dialog. Sem isto, o texto dos inputs MUI fica branco (invisivel).
	const temaPrincipal = useMainTheme();
	const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down("md"));

	const [grupoSelecionadoId, setGrupoSelecionadoId] = useState<number | null>(null);
	const [buscaGrupo, setBuscaGrupo] = useState("");
	const [buscaPermissao, setBuscaPermissao] = useState("");
	const [somenteAtivas, setSomenteAtivas] = useState(false);
	const [estadoAtual, setEstadoAtual] = useState<Map<string, boolean>>(new Map());
	const [snapshot, setSnapshot] = useState<Map<string, boolean>>(new Map());
	const [gruposAbertos, setGruposAbertos] = useState(false);
	const [confirmarSalvarAberto, setConfirmarSalvarAberto] = useState(false);
	const [descarteAberto, setDescarteAberto] = useState(false);
	const [auditoriaAberta, setAuditoriaAberta] = useState(false);
	const [auditoriaFiltros, setAuditoriaFiltros] = useState(FILTROS_AUDITORIA_INICIAL);

	// -------------------------------- QUERIES -------------------------------
	const gruposQuery = useListarGruposUsuario({ tag_recurso: tagRecurso }, aberto);
	const grupos = useMemo(() => gruposQuery.data ?? [], [gruposQuery.data]);
	const gruposFiltrados = useMemo(() => filtrarGruposPorBusca(grupos, buscaGrupo), [grupos, buscaGrupo]);
	const grupoSelecionado = useMemo(
		() => grupos.find((grupo) => grupo.id === grupoSelecionadoId) ?? null,
		[grupos, grupoSelecionadoId],
	);

	const req = useMemo<ISpaceVerificaRecursoRequest>(
		() => ({ tipo: "GRUPO", id_cad_grupo_usuario: grupoSelecionadoId ?? 0, tag_recurso: tagRecurso }),
		[grupoSelecionadoId, tagRecurso],
	);

	const recursosQuery = useVerificaRecursosDaTela(req, aberto && grupoSelecionadoId != null && Boolean(tagRecurso));
	const salvarMutation = useSalvarRestricoesGrupo();

	const auditoriaQuery = useListarAuditoriaRecurso(
		{
			tag_recurso: tagRecurso,
			page: auditoriaFiltros.pagina,
			limit: LIMITE_AUDITORIA_POR_PAGINA,
			tipo: auditoriaFiltros.tipo === "todos" ? undefined : auditoriaFiltros.tipo,
			data_inicio: auditoriaFiltros.dataInicio || undefined,
			data_fim: auditoriaFiltros.dataFim || undefined,
		},
		aberto && auditoriaAberta && Boolean(tagRecurso),
	);

	// -------------------------------- EFFECTS -------------------------------
	// Seleciona o grupo do usuario logado (ou o primeiro) assim que a lista carrega.
	useEffect(() => {
		if (grupoSelecionadoId != null || grupos.length === 0) return;
		const grupoUsuario = grupos.find((grupo) => grupo.id === user?.id_cad_grupo_usuario);
		setGrupoSelecionadoId(grupoUsuario?.id ?? grupos[0].id);
	}, [grupos, grupoSelecionadoId, user?.id_cad_grupo_usuario]);

	// Reseta a selecao ao fechar para reabrir limpo.
	useEffect(() => {
		if (!aberto) {
			setGrupoSelecionadoId(null);
			setBuscaGrupo("");
			setBuscaPermissao("");
			setSomenteAtivas(false);
			setGruposAbertos(false);
			setConfirmarSalvarAberto(false);
			setDescarteAberto(false);
			setAuditoriaAberta(false);
			setAuditoriaFiltros(FILTROS_AUDITORIA_INICIAL);
		}
	}, [aberto]);

	// Quando os dados do recurso carregam/atualizam, redefine o estado de edicao e o snapshot salvo.
	useEffect(() => {
		const data = recursosQuery.data;
		if (!data) return;
		const mapa = construirMapaEstado(AdapterRecursoDaTelaComoArvore(data));
		setEstadoAtual(new Map(mapa));
		setSnapshot(new Map(mapa));
	}, [recursosQuery.data]);

	// ----------------------------- DERIVED STATE ----------------------------
	const data = recursosQuery.data;
	const arvore = useMemo(() => montarArvoreRecurso(data), [data]);

	const totalPermissoes = data?.permissoes.length ?? 0;
	const permissoesAtivas = contarPermissoesAtivas(data, estadoAtual);
	const grupoNome = grupoSelecionado?.descricao ?? "Selecione um grupo";
	const grupoIdLabel = grupoSelecionado ? String(grupoSelecionado.id) : "—";

	const delta = useMemo(() => calcularDelta(arvore, snapshot, estadoAtual), [arvore, snapshot, estadoAtual]);
	const resumo = useMemo(
		() => montarResumoAlteracoes(arvore, snapshot, estadoAtual),
		[arvore, snapshot, estadoAtual],
	);
	const temAlteracoes = resumo.total > 0;
	const salvarBloqueado = recursosQuery.isFetching || !recursosQuery.isSuccess || grupoSelecionadoId == null;

	const podeEditar =
		permissaoSalvar.permitido && !permissaoSalvar.carregando && !salvarMutation.isPending && !salvarBloqueado;
	const podeSalvar = podeEditar && temAlteracoes;

	// -------------------------------- HANDLERS ------------------------------
	const aoAlternar = (tag: string) => {
		if (!podeEditar) return;
		setEstadoAtual((atual) => {
			const proximo = new Map(atual);
			proximo.set(tag, !(proximo.get(tag) ?? false));
			return proximo;
		});
	};

	const aoDefinirPermissoes = (tags: string[], ativo: boolean) => {
		if (!podeEditar) return;
		setEstadoAtual((atual) => {
			const proximo = new Map(atual);
			tags.forEach((tag) => proximo.set(tag, ativo));
			return proximo;
		});
	};

	const aoConfirmarSalvar = () => {
		if (!podeSalvar || grupoSelecionadoId == null || !confirmarSalvarAberto) return;

		salvarMutation.mutate(
			{ id_cad_grupo_usuario: grupoSelecionadoId, recursos: delta.recursos, permissoes: delta.permissoes },
			{
				onSuccess: () => {
					setSnapshot(new Map(estadoAtual));
					setConfirmarSalvarAberto(false);
					enqueueSnackbar("Permissões salvas com sucesso.", { variant: "success" });
					void queryClient.invalidateQueries({
						queryKey: keys_permissoes.verificaRecursosDaTelaCompleto(req),
					});
				},
			},
		);
	};

	const aoDescartar = () => {
		setEstadoAtual(new Map(snapshot));
		setDescarteAberto(false);
	};
	const aoAbrirConfirmacaoSalvar = () => {
		if (!podeSalvar) return;
		setConfirmarSalvarAberto(true);
	};

	const aoPesquisarAuditoria = (filtros: FiltroAuditoriaAplicado) => setAuditoriaFiltros({ ...filtros, pagina: 1 });

	const aoMudarPaginaAuditoria = (pagina: number) => setAuditoriaFiltros((atual) => ({ ...atual, pagina }));

	const carregando = recursosQuery.isLoading && grupoSelecionadoId != null;
	const isMesmoGrupo = grupoSelecionadoId === user?.id_cad_grupo_usuario;
	const recursoNome = data?.recurso.descricao ?? "";

	// -------------------------------- RETURN --------------------------------
	return (
		<ThemeProvider theme={temaPrincipal}>
			<AppDialogDefault
				aberto={aberto}
				onFechar={onFechar}
				titulo="Restrições da tela"
				subtitulo={data?.recurso.descricao}
				icone="lucide:shield"
				maxWidth="lg"
				exibirBotaoFecharRodape={false}
				impedirFecharBackdrop
				paperSx={{ height: { md: "min(80vh, 760px)" } }}
				acoesRodape={
					<RodapeAcoesModal
						permissaoSalvar={permissaoSalvar}
						salvarBloqueado={salvarBloqueado}
						temAlteracoes={temAlteracoes}
						salvando={salvarMutation.isPending}
						onDescartar={() => setDescarteAberto(true)}
						onCancelar={onFechar}
						onSalvar={aoAbrirConfirmacaoSalvar}
					/>
				}
			>
				<CorpoRestricoesModal
					isMobile={isMobile}
					gruposAbertos={gruposAbertos}
					sidebar={{
						grupos: gruposFiltrados,
						totalGrupos: grupos.length,
						carregando: gruposQuery.isLoading,
						buscaGrupo,
						grupoSelecionadoId,
						temAlteracoes,
						onBuscar: setBuscaGrupo,
						onSelecionar: setGrupoSelecionadoId,
					}}
					toolbar={{
						grupoIdLabel,
						grupoNome,
						totalPermissoes,
						permissoesAtivas,
						buscaPermissao,
						somenteAtivas,
						temAlteracoes,
						qtdAlteracoes: resumo.total,
						onBuscarPermissao: setBuscaPermissao,
						onAlternarSomenteAtivas: () => setSomenteAtivas((valor) => !valor),
						onAuditoria: tagRecurso ? () => setAuditoriaAberta(true) : undefined,
					}}
					conteudo={{
						edicaoBloqueada: !podeEditar,
						carregando,
						erro: recursosQuery.isError,
						data,
						estado: estadoAtual,
						isMesmoGrupo,
						buscaPermissao,
						somenteAtivas,
						onAlternar: aoAlternar,
						onDefinirPermissoes: aoDefinirPermissoes,
					}}
					onAbrirGrupos={() => setGruposAbertos(true)}
					onFecharGrupos={() => setGruposAbertos(false)}
					onSelecionarGrupoMobile={(id) => {
						setGrupoSelecionadoId(id);
						setGruposAbertos(false);
					}}
				/>
			</AppDialogDefault>

			<DialogConfirmarSalvar
				bloqueado={salvarBloqueado}
				permissao={permissaoSalvar}
				aberto={confirmarSalvarAberto}
				salvando={salvarMutation.isPending}
				grupoNome={grupoNome}
				resumo={resumo}
				acoes={{
					onFechar: () => setConfirmarSalvarAberto(false),
					onConfirmar: aoConfirmarSalvar,
				}}
			/>

			<DialogConfirmarDescarte
				aberto={descarteAberto}
				grupoNome={grupoNome}
				resumo={resumo}
				acoes={{
					onFechar: () => setDescarteAberto(false),
					onConfirmar: aoDescartar,
				}}
			/>

			<ModalAuditoriaGrupo
				aberto={auditoriaAberta}
				grupo={{ id: 0, descricao: recursoNome }}
				itens={auditoriaQuery.data?.items ?? []}
				carregando={auditoriaQuery.isLoading}
				paginacao={{
					pagina: auditoriaFiltros.pagina,
					totalPaginas: auditoriaQuery.data?.total_pages ?? 0,
					total: auditoriaQuery.data?.total ?? 0,
				}}
				acoes={{
					onFechar: () => setAuditoriaAberta(false),
					onMudarPagina: aoMudarPaginaAuditoria,
					onPesquisar: aoPesquisarAuditoria,
				}}
			/>
		</ThemeProvider>
	);
}
